import {
  Plus, Trash2, Calendar, ChevronDown, ChevronUp,
  FileText, Users, BarChart3, Download, RotateCcw, ClipboardList, X, GitBranch, BookOpen, Search,
  AlertTriangle,
} from 'lucide-react'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { SequenceDiagram } from '../../dashboards/components/ClientSequenceDiagram/SequenceDiagram'
import { parseDsl, DIAGRAM_TEMPLATES } from '../../dashboards/components/ClientSequenceDiagram/dsl-parser'
import { useScopeStore } from '../store'
import { getSectionById } from '../schema'
import { sowTemplateApi, sequenceDiagramLibraryApi, type SowTemplateType } from '@api/client'
import { getDefaultSowConfig } from '../utils/defaults'
import { mergeSowWithTemplate } from '../utils/sowSections'
import { substituteLegalTextPlaceholders } from '../utils/sowLegalText'
import type { SowSectionKey } from '../utils/sowSections'
import type { SowConfig } from '../types'
import type { RaciValue, GanttParty } from '../types'

// ── Multi-diagram helpers ───────────────────────────────────────────────────

const DIAGRAM_BREAK = '===DIAGRAM BREAK==='

function parseSowDiagrams(raw: string): string[] {
  if (!raw?.trim()) return []
  return raw.split(DIAGRAM_BREAK).map(s => s.trim()).filter(Boolean)
}

function serializeSowDiagrams(dsls: string[]): string {
  return dsls.filter(Boolean).join(`\n${DIAGRAM_BREAK}\n`)
}

// ── Legend item ────────────────────────────────────────────────────────────

function LegendItem({
  color,
  dash,
  label,
  badge,
}: {
  color: string
  dash: string | false
  label: string
  badge?: string
}) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
      <svg width="32" height="10" style={{ flexShrink: 0 }}>
        <line
          x1="0" y1="5" x2="25" y2="5"
          stroke={color} strokeWidth="1.5"
          strokeDasharray={dash || undefined}
        />
        <polygon points="25,5 19,2 19,8" fill={color} />
      </svg>
      {badge && (
        <span className="px-1 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: color, lineHeight: 1 }}>
          {badge}
        </span>
      )}
      {label}
    </span>
  )
}

// ── SowSection ─────────────────────────────────────────────────────────────

function SowSection({
  id,
  icon: Icon,
  iconColor,
  title,
  expanded,
  onToggle,
  children,
}: {
  id: string
  icon: React.ElementType
  iconColor: string
  title: string
  expanded: boolean
  onToggle: (id: string) => void
  children: React.ReactNode
}) {
  return (
    <div className="bg-[var(--bg-tertiary)]/40 border border-[var(--border-color)] rounded-lg">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-secondary)] transition-colors"
        onClick={() => onToggle(id)}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className={iconColor} />
          <h4 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h4>
        </div>
        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {expanded && (
        <div className="p-4 border-t border-[var(--border-color)] space-y-4">{children}</div>
      )}
    </div>
  )
}

const RACI_OPTIONS: { value: RaciValue; label: string }[] = [
  { value: '', label: '-' },
  { value: 'R', label: 'Responsible' },
  { value: 'A', label: 'Accountable' },
  { value: 'C', label: 'Consulted' },
  { value: 'I', label: 'Informed' },
]

const RACI_COLORS: Record<string, string> = {
  R: 'bg-blue-100 text-blue-800',
  A: 'bg-green-100 text-green-800',
  C: 'bg-yellow-100 text-yellow-800',
  I: 'bg-gray-100 text-gray-600',
  '': '',
}

export function SowTab() {
  const {
    scope,
    updateSowConfig,
    addSowMilestone,
    updateSowMilestone,
    removeSowMilestone,
    setSowPhaseDatesByIndex,
    addGanttTask,
    updateGanttTask,
    removeGanttTask,
    reorderGanttTask,
    loadDefaultGantt,
    addRaciColumn,
    removeRaciColumn,
    updateRaciColumnName,
    addRaciEntry,
    updateRaciAssignment,
    removeRaciEntry,
    loadDefaultRaci,
  } = useScopeStore()

  const {
    sowTemplate,
    setSowTemplate,
    resetSowSection,
  } = useScopeStore()

  const sowCustomizedSections = scope.sowCustomizedSections

  const { sowConfig, generalInfo, kybConfig } = scope
  const disabled = scope.finalized

  // Library of saved sequence diagrams
  const [library, setLibrary] = useState<Array<{id: number; name: string; description: string | null; dsl: string}>>([])

  useEffect(() => {
    sequenceDiagramLibraryApi.list().then((res) => {
      if (res.success && Array.isArray(res.data)) setLibrary(res.data)
    }).catch(() => {})
  }, [])

  // Derive SOW template type from scope settings
  const sowTemplateType: SowTemplateType = generalInfo.isUpsell
    ? 'upsell'
    : generalInfo.resellerAgreement === 'yes'
      ? generalInfo.resellerRelationshipType === 'selling_to_customers'
        ? 'reseller_parent'
        : generalInfo.resellerRelationshipType === 'customer_of_reseller'
          ? 'reseller_child'
          : 'global'
      : 'global'

  // Fetch SOW template on mount (and when type changes)
  useEffect(() => {
    let cancelled = false
    sowTemplateApi.get(sowTemplateType).then((res) => {
      if (cancelled) return
      const defaults = getDefaultSowConfig(sowTemplateType)
      const templateConfig: SowConfig = { ...defaults, ...(res.data?.config as Partial<SowConfig> ?? {}) }
      setSowTemplate(templateConfig)
      // Merge inherited sections into scope SOW data (without marking sections as customized)
      const currentState = useScopeStore.getState()
      const merged = mergeSowWithTemplate(currentState.scope.sowConfig, templateConfig, currentState.scope.sowCustomizedSections)
      useScopeStore.setState((state) => ({
        scope: { ...state.scope, sowConfig: merged },
      }))
    }).catch(() => {
      // Silently fail — template is optional
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowTemplateType])

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    legal: false,
    deliveryNotes: true,
    phasesVariation: true,
    commercials: true,
    sequenceDiagram: false,
    raci: false,
    gantt: false,
    keyAssumptions: false,
  })

  // Sequence diagram state
  const [diagramPreviewWidth, setDiagramPreviewWidth] = useState(0)
  const [expandedDiagramIdx, setExpandedDiagramIdx] = useState<number | null>(null)
  const [diagramPickerOpen, setDiagramPickerOpen] = useState(false)
  const [diagramPickerTab, setDiagramPickerTab] = useState<'default' | 'library'>('library')
  const [diagramPickerSearch, setDiagramPickerSearch] = useState('')
  const diagramPickerRef = useRef<HTMLDivElement>(null)

  const sequenceDiagramFlows = useMemo(() => {
    const dsls = parseSowDiagrams(sowConfig.sequenceDiagramDsl || '')
    return dsls.map(dsl => {
      const result = parseDsl('sow-tool', dsl)
      return result.flow.participants.length > 0 ? { flow: result.flow, dsl } : null
    }).filter((x): x is { flow: ReturnType<typeof parseDsl>['flow']; dsl: string } => x !== null)
  }, [sowConfig.sequenceDiagramDsl])

  // Callback ref — attaches ResizeObserver whenever the probe element mounts
  // (including when the collapsed section is first expanded)
  const diagramProbeRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setDiagramPreviewWidth(entry.contentRect.width))
    ro.observe(el)
    // Capture current width immediately
    setDiagramPreviewWidth(el.getBoundingClientRect().width)
  }, [])

  useEffect(() => {
    if (expandedDiagramIdx === null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpandedDiagramIdx(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expandedDiagramIdx])

  useEffect(() => {
    if (!diagramPickerOpen) return
    function handler(e: MouseEvent) {
      if (diagramPickerRef.current && !diagramPickerRef.current.contains(e.target as Node))
        setDiagramPickerOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [diagramPickerOpen])


  // Schema lookups
  const legalSection = getSectionById('sow_legal_text')
  const deliverySection = getSectionById('sow_delivery_notes')
  const phasesSection = getSectionById('sow_phases_variation')
  const raciSection = getSectionById('sow_raci_chart')
  const ganttSection = getSectionById('sow_gantt_chart')
  const keyAssumptionsSection = getSectionById('sow_key_assumptions')

  const handleToggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  // Helper: SOW section header with template status badge and reset button
  const SowSectionHeader = ({
    sectionKey,
    title,
    children,
  }: {
    sectionKey: SowSectionKey
    title: string
    children?: React.ReactNode
  }) => {
    const isCustomized = sowCustomizedSections.includes(sectionKey)
    return (
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium text-[var(--text-primary)]">{children ?? title}</span>
        {sowTemplate && (
          <>
            {isCustomized ? (
              <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                Customized
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                From Template
              </span>
            )}
            {isCustomized && !disabled && (
              <button
                type="button"
                className="flex items-center gap-1 px-2 py-0.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] rounded transition-colors"
                onClick={() => resetSowSection(sectionKey)}
              >
                <RotateCcw size={12} />
                Reset to Template
              </button>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium text-[var(--text-primary)]">Statement of Work</h3>
          {sowTemplateType !== 'global' && (
            <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
              {sowTemplateType === 'upsell' ? 'Upsell Template' : sowTemplateType === 'reseller_parent' ? 'Reseller Parent Template' : sowTemplateType === 'reseller_child' ? 'Reseller Child Template' : 'Global Template'}
            </span>
          )}
        </div>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Define delivery approach, timeline, RACI, Gantt, and payment terms for the SOW.
        </p>
      </div>

      {/* 1. Legal Text */}
      <SowSection id="legal" expanded={expandedSections.legal} onToggle={handleToggleSection} icon={FileText} iconColor="text-primary" title={legalSection?.formTitle || 'Legal Text'}>
        <SowSectionHeader sectionKey="legal" title="Legal Text" />
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">SOW Legal Text</label>
          <p className="text-xs text-[var(--text-muted)] mb-2">
            Standard SOW preamble, scope of services, and out-of-scope clauses. Leave <code>[Client Name]</code> and <code>[ABN]</code> as tokens — they are substituted in the preview below using the scope's Customer name and ABN. Replace <code>[Insert Date]</code> with the actual effective date before finalising.
          </p>
          <textarea
            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 font-mono leading-relaxed"
            rows={24}
            value={sowConfig.legalText || ''}
            onChange={(e) => updateSowConfig('legalText', e.target.value)}
            disabled={disabled}
            placeholder="This Statement of Work (&quot;SOW&quot;) is issued under the terms of..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Preview</label>
          <p className="text-xs text-[var(--text-muted)] mb-2">
            Read-only preview with the scope's Customer name{scope.customerAbn ? ' and ABN' : ''} substituted in.
          </p>
          <div className="w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-sm text-[var(--text-primary)] font-mono leading-relaxed whitespace-pre-wrap">
            {substituteLegalTextPlaceholders(sowConfig.legalText || '', {
              customerName: scope.customerName,
              customerAbn: scope.customerAbn,
            })}
          </div>
        </div>
      </SowSection>

      {/* 2. Delivery Notes */}
      <SowSection id="deliveryNotes" expanded={expandedSections.deliveryNotes} onToggle={handleToggleSection} icon={Users} iconColor="text-primary" title={deliverySection?.formTitle || 'Delivery Notes'}>
        <SowSectionHeader sectionKey="deliveryNotes" title="Delivery Notes" />
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Implementations Manager</label>
          <select
            className="w-full md:w-64 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
            value={sowConfig.implementationsManager || ''}
            onChange={(e) => updateSowConfig('implementationsManager', e.target.value)}
            disabled={disabled}
          >
            <option value="">Select manager...</option>
            <option value="Aju">Aju</option>
            <option value="Boris">Boris</option>
            <option value="Jashinta">Jashinta</option>
            <option value="Andrew">Andrew</option>
            <option value="Priya">Priya</option>
            <option value="Shiv">Shiv</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Delivery Notes</label>
          <textarea
            className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
            placeholder="e.g., Agile delivery with 2-week sprints, weekly status reports, dedicated project channel..."
            value={sowConfig.deliveryNotes || ''}
            onChange={(e) => updateSowConfig('deliveryNotes', e.target.value)}
            disabled={disabled}
          />
        </div>
        {(() => {
          const prodDate = (generalInfo.productionAccessDate || '').trim()
          const goLiveDate = (generalInfo.goLiveDate || '').trim()
          if (!prodDate && !goLiveDate) return null
          return (
            <div className="bg-[var(--bg-tertiary)]/60 border border-[var(--border-color)] rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] mb-3">
                Key Dates (from General tab — read only)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-[var(--text-muted)] mb-1">Production access date</div>
                  <div className="text-[var(--text-primary)] font-medium">
                    {prodDate || <span className="text-[var(--text-muted)] italic font-normal">Not set</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[var(--text-muted)] mb-1">Expected go-live date</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--text-primary)] font-medium">
                      {goLiveDate || <span className="text-[var(--text-muted)] italic font-normal">Not set</span>}
                    </span>
                    {goLiveDate && (
                      <span
                        className={
                          generalInfo.isHardGoLiveDate
                            ? 'px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 border border-red-200'
                            : 'px-2 py-0.5 text-xs font-medium rounded bg-amber-50 text-amber-700 border border-amber-200'
                        }
                      >
                        {generalInfo.isHardGoLiveDate ? 'Hard date' : 'Soft date'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </SowSection>

      {/* 3. Phases & Variation */}
      <SowSection id="phasesVariation" expanded={expandedSections.phasesVariation} onToggle={handleToggleSection} icon={Calendar} iconColor="text-primary" title={phasesSection?.formTitle || 'Phases & Variation'}>
        <SowSectionHeader sectionKey="phases" title="Phases & Variation" />
        {/* Milestones */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-[var(--text-primary)]">Key Milestones</label>
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              onClick={addSowMilestone}
              disabled={disabled}
            >
              <Plus size={14} />
              Add Milestone
            </button>
          </div>
          {sowConfig.milestones.length === 0 ? (
            <div className="text-sm text-[var(--text-muted)] text-center py-6 border border-dashed border-[var(--border-color)] rounded-lg">
              No milestones added yet.
            </div>
          ) : (
            <div className="space-y-3">
              {sowConfig.milestones.map((m) => (
                <div key={m.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input type="text" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50" placeholder="Milestone name" value={m.name} onChange={(e) => updateSowMilestone(m.id, { name: e.target.value })} disabled={disabled} />
                      <input type="date" className="h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50" value={m.date} onChange={(e) => updateSowMilestone(m.id, { date: e.target.value })} disabled={disabled} />
                    </div>
                    <button type="button" className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-colors disabled:opacity-50" onClick={() => removeSowMilestone(m.id)} disabled={disabled}><Trash2 size={16} /></button>
                  </div>
                  <textarea className="w-full min-h-20 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y" placeholder="Milestone description" value={m.description} onChange={(e) => updateSowMilestone(m.id, { description: e.target.value })} disabled={disabled} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Project Phases — descriptions sourced from General tab, dates editable here */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-[var(--text-primary)]">Project Phases</label>
            <span className="text-xs text-[var(--text-muted)]">
              Phase descriptions are pulled from the General tab. Set dates here.
            </span>
          </div>
          {generalInfo.phases.every((p) => !p.trim()) ? (
            <div className="text-sm text-[var(--text-muted)] text-center py-6 border border-dashed border-[var(--border-color)] rounded-lg">
              No phase scopes defined on the General tab yet.
            </div>
          ) : (
            <div className="space-y-3">
              {generalInfo.phases.map((phaseText, idx) => {
                if (!phaseText.trim()) return null
                const sowPhase = sowConfig.phases[idx]
                return (
                  <div key={idx} className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">Phase {idx + 1}</span>
                      <span className="text-xs text-[var(--text-muted)]">From General tab (read-only)</span>
                    </div>
                    <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap bg-[var(--bg-secondary)]/40 border border-[var(--border-color)] rounded px-3 py-2">
                      {phaseText}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">Start Date</label>
                        <input
                          type="date"
                          className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          value={sowPhase?.startDate || ''}
                          onChange={(e) => setSowPhaseDatesByIndex(idx, { startDate: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">End Date</label>
                        <input
                          type="date"
                          className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          value={sowPhase?.endDate || ''}
                          onChange={(e) => setSowPhaseDatesByIndex(idx, { endDate: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* SOW Variation Note (from General) */}
        {generalInfo.sowVariationNote?.trim() && (
          <div className="border border-dashed border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]/30">
            <span className="text-xs font-medium text-[var(--text-muted)]">SOW Variation Note (from General tab)</span>
            <p className="text-sm text-[var(--text-primary)] mt-1 whitespace-pre-wrap">{generalInfo.sowVariationNote}</p>
          </div>
        )}
      </SowSection>

      {/* Width probe for diagram zoom-to-fit — outside the section so it's always in the DOM */}
      <div ref={diagramProbeRef} style={{ width: '100%', height: 0, overflow: 'hidden', pointerEvents: 'none' }} />

      {/* 4. Commercials */}
      <SowSection id="commercials" expanded={expandedSections.commercials} onToggle={handleToggleSection} icon={BarChart3} iconColor="text-green-500" title="Commercials">
        <SowSectionHeader sectionKey="commercials" title="Commercials" />
        <div className="space-y-4">
          <label className="block text-sm font-medium text-[var(--text-primary)]">Fees and Payment Terms</label>

          <div className="flex gap-3">
            {([
              { value: 'fixed', label: 'Fixed Fee' },
              { value: 'tm', label: 'Time & Materials (T&M)' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => updateSowConfig('commercialsPricingModel', opt.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                  sowConfig.commercialsPricingModel === opt.value
                    ? 'border-green-500 bg-green-500/10 text-[var(--text-primary)] font-medium'
                    : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-500 hover:text-[var(--text-primary)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {sowConfig.commercialsPricingModel === 'fixed' && (
            <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-3 bg-[var(--bg-secondary)]/40">
              <p className="text-xs text-[var(--text-muted)] italic">
                The total fixed fee for the Professional Services described in this SOW is AUD $[amount], payable as per the Agreement.
              </p>
              <div>
                <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Total Fixed Fee (AUD)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                  <input
                    type="text"
                    disabled={disabled}
                    className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="e.g. 25,000"
                    value={sowConfig.commercialsFixedFee || ''}
                    onChange={(e) => updateSowConfig('commercialsFixedFee', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {sowConfig.commercialsPricingModel === 'tm' && (
            <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-3 bg-[var(--bg-secondary)]/40">
              <p className="text-xs text-[var(--text-muted)] italic">
                Services under this SOW will be billed on a T&M basis at the rates below. Total fees will not exceed the cap without prior written approval.
              </p>
              <div>
                <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Hourly Rate (AUD)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                  <input
                    type="text"
                    disabled={disabled}
                    className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="e.g. 250"
                    value={sowConfig.commercialsTmRate || ''}
                    onChange={(e) => updateSowConfig('commercialsTmRate', e.target.value)}
                  />
                  <span className="text-sm text-[var(--text-muted)]">/ hour</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Total Fee Cap (AUD)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-muted)]">AUD $</span>
                  <input
                    type="text"
                    disabled={disabled}
                    className="flex-1 h-9 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder="e.g. 50,000"
                    value={sowConfig.commercialsTmCap || ''}
                    onChange={(e) => updateSowConfig('commercialsTmCap', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {!sowConfig.commercialsPricingModel && (
            <p className="text-xs text-[var(--text-muted)] italic py-2">Select a pricing model above to configure fee terms.</p>
          )}
        </div>
      </SowSection>

      {/* 5. Integration Sequence Diagram */}
      <SowSection id="sequenceDiagram" expanded={expandedSections.sequenceDiagram} onToggle={handleToggleSection} icon={GitBranch} iconColor="text-teal-500" title="Integration Sequence Diagram">
        <div className="space-y-4">
          {/* Load button — always visible */}
          {!disabled && (
            <div className="flex items-center gap-2" ref={diagramPickerRef}>
              <div className="relative">
                <button
                  type="button"
                  disabled={disabled}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors disabled:opacity-50"
                  onClick={() => setDiagramPickerOpen((v) => !v)}
                >
                  <BookOpen size={14} />
                  {sequenceDiagramFlows.length > 0 ? 'Add Diagram' : 'Load Diagram'}
                  <ChevronDown size={12} className={`transition-transform ${diagramPickerOpen ? 'rotate-180' : ''}`} />
                </button>
                {diagramPickerOpen && (
                  <div className="absolute top-full left-0 mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-xl w-96 overflow-hidden">
                    {/* Tabs */}
                    <div className="flex border-b border-[var(--border-color)]">
                      {(['library', 'default'] as const).map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setDiagramPickerTab(tab)}
                          className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
                            diagramPickerTab === tab
                              ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-500/5'
                              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                          }`}
                        >
                          {tab === 'library' ? 'Saved Library' : 'Default Templates'}
                        </button>
                      ))}
                    </div>

                    {diagramPickerTab === 'default' && (
                      <div className="max-h-72 overflow-y-auto">
                        {DIAGRAM_TEMPLATES.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors border-b border-[var(--border-color)] last:border-0"
                            onClick={() => {
                              const existing = parseSowDiagrams(sowConfig.sequenceDiagramDsl || '')
                              updateSowConfig('sequenceDiagramDsl', serializeSowDiagrams([...existing, tpl.dsl]))
                              setDiagramPickerOpen(false)
                            }}
                          >
                            <p className="text-sm font-semibold text-[var(--text-primary)]">{tpl.label}</p>
                            <p className="text-xs text-[var(--text-muted)] mt-0.5">{tpl.description}</p>
                          </button>
                        ))}
                      </div>
                    )}

                    {diagramPickerTab === 'library' && (
                      <div className="max-h-72 overflow-y-auto">
                        {library.length > 0 && (
                          <div className="px-3 py-2 border-b border-[var(--border-color)] sticky top-0 bg-[var(--bg-primary)]">
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
                              <Search size={12} className="text-[var(--text-muted)] shrink-0" />
                              <input
                                autoFocus
                                className="flex-1 bg-transparent text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                                placeholder="Search saved diagrams..."
                                value={diagramPickerSearch}
                                onChange={(e) => setDiagramPickerSearch(e.target.value)}
                              />
                              {diagramPickerSearch && (
                                <button type="button" onClick={() => setDiagramPickerSearch('')} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                                  <X size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {library.length === 0 ? (
                          <p className="px-4 py-6 text-xs text-[var(--text-muted)] text-center">No saved diagrams yet. Build one in the Sequence Diagrams tool.</p>
                        ) : library.filter((e) => !diagramPickerSearch.trim() || e.name.toLowerCase().includes(diagramPickerSearch.toLowerCase()) || (e.description || '').toLowerCase().includes(diagramPickerSearch.toLowerCase())).length === 0 ? (
                          <p className="px-4 py-6 text-xs text-[var(--text-muted)] text-center">No matches for "{diagramPickerSearch}".</p>
                        ) : (
                          library.filter((e) => !diagramPickerSearch.trim() || e.name.toLowerCase().includes(diagramPickerSearch.toLowerCase()) || (e.description || '').toLowerCase().includes(diagramPickerSearch.toLowerCase())).map((entry) => (
                            <button
                              key={entry.id}
                              type="button"
                              className="w-full text-left px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors border-b border-[var(--border-color)] last:border-0"
                              onClick={() => {
                                const existing = parseSowDiagrams(sowConfig.sequenceDiagramDsl || '')
                                updateSowConfig('sequenceDiagramDsl', serializeSowDiagrams([...existing, entry.dsl]))
                                setDiagramPickerOpen(false)
                              }}
                            >
                              <p className="text-sm font-semibold text-[var(--text-primary)]">{entry.name}</p>
                              {entry.description && <p className="text-xs text-[var(--text-muted)] mt-0.5">{entry.description}</p>}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {sequenceDiagramFlows.length > 0 && (
                <button
                  type="button"
                  onClick={() => { if (confirm('Clear all diagrams?')) updateSowConfig('sequenceDiagramDsl', '') }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--text-muted)] hover:text-red-500 transition-colors"
                >
                  <X size={13} /> Clear all
                </button>
              )}
            </div>
          )}

          {/* Empty state */}
          {sequenceDiagramFlows.length === 0 && (
            <p className="text-xs text-[var(--text-muted)]">Select a diagram to attach to this client's SOW.</p>
          )}

          {/* Diagram cards */}
          {sequenceDiagramFlows.map(({ flow }, idx) => {
            const naturalW = flow.participants.length * 230
            const scale = naturalW > diagramPreviewWidth ? diagramPreviewWidth / naturalW : 1
            return (
              <div key={idx} className="rounded-xl border border-[var(--border-color)] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-semibold text-[var(--text-muted)] shrink-0">Diagram {idx + 1} —</span>
                    <input
                      type="text"
                      className="text-xs font-semibold text-[var(--text-primary)] bg-transparent border-b border-transparent hover:border-[var(--border-color)] focus:border-primary focus:outline-none flex-1 min-w-0 py-0.5 disabled:border-transparent"
                      value={flow.title || ''}
                      onChange={(e) => {
                        const existing = parseSowDiagrams(sowConfig.sequenceDiagramDsl || '')
                        const dsl = existing[idx]
                        if (!dsl) return
                        const newTitle = e.target.value
                        const updated = dsl.match(/^title\s+/m)
                          ? dsl.replace(/^title\s+.*$/m, `title ${newTitle}`)
                          : `title ${newTitle}\n${dsl}`
                        existing[idx] = updated
                        updateSowConfig('sequenceDiagramDsl', serializeSowDiagrams(existing))
                      }}
                      placeholder="Untitled diagram"
                      disabled={disabled}
                    />
                  </div>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => {
                        const existing = parseSowDiagrams(sowConfig.sequenceDiagramDsl || '')
                        updateSowConfig('sequenceDiagramDsl', serializeSowDiagrams(existing.filter((_, i) => i !== idx)))
                      }}
                      className="text-[var(--text-muted)] hover:text-red-500 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
                {/* Preview */}
                <div
                  className="rounded-b-xl bg-white overflow-hidden cursor-pointer"
                  style={{ minHeight: 80 }}
                  onClick={() => setExpandedDiagramIdx(idx)}
                >
                  <div style={{ zoom: scale, pointerEvents: 'none' }}>
                    <SequenceDiagram flow={flow} />
                  </div>
                </div>
                {/* Legend */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-3 py-2 border-t border-[var(--border-color)]">
                  <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Legend</span>
                  <LegendItem color="#4338ca" dash={false} label="Request" />
                  <LegendItem color="#9ca3af" dash="5,4" label="Return / Response" />
                  <LegendItem color="#4338ca" dash="5,4" label="Async (webhook)" badge="ASYNC" />
                </div>
              </div>
            )
          })}
        </div>

        {/* Fullscreen modal */}
        {expandedDiagramIdx !== null && sequenceDiagramFlows[expandedDiagramIdx] && (
          <div
            className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-sm"
            onClick={() => setExpandedDiagramIdx(null)}
          >
            <div
              className="relative flex flex-col bg-white rounded-2xl shadow-2xl m-4 overflow-hidden flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                <p className="text-sm font-semibold text-gray-800">
                  {sequenceDiagramFlows[expandedDiagramIdx].flow.title || 'Integration Sequence Diagram'}
                </p>
                <button
                  type="button"
                  onClick={() => setExpandedDiagramIdx(null)}
                  className="w-8 h-8 grid place-items-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="overflow-auto flex-1 p-4">
                <SequenceDiagram flow={sequenceDiagramFlows[expandedDiagramIdx].flow} />
              </div>
            </div>
          </div>
        )}
      </SowSection>

      {/* 5. RACI Chart */}
      <SowSection id="raci" expanded={expandedSections.raci} onToggle={handleToggleSection} icon={Users} iconColor="text-purple-500" title={raciSection?.formTitle || 'RACI Chart'}>
        <SowSectionHeader sectionKey="raci" title="RACI Chart" />
        <p className="text-xs text-[var(--text-muted)]">
          R = Responsible, A = Accountable, C = Consulted, I = Informed
        </p>

        {/* Load template button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
            onClick={() => {
              if (sowConfig.raciEntries.length > 0) {
                if (!confirm('This will replace the current RACI chart with the default template. Continue?')) return
              }
              loadDefaultRaci()
            }}
            disabled={disabled}
          >
            <Download size={14} />
            Load Default Template
          </button>
          {!disabled && (
            <>
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
                onClick={() => addRaciColumn('New Role')}
              >
                <Plus size={14} />
                Add Column
              </button>
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
                onClick={addRaciEntry}
              >
                <Plus size={14} />
                Add Row
              </button>
            </>
          )}
        </div>

        {/* RACI table */}
        {sowConfig.raciColumns.length === 0 && sowConfig.raciEntries.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] text-center py-8 border border-dashed border-[var(--border-color)] rounded-lg">
            No RACI chart yet. Click "Load Default Template" to get started or add columns and rows manually.
          </div>
        ) : (
          <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-secondary)]">
                  <th className="text-left px-3 py-2 border-b border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[200px]">Task</th>
                  {sowConfig.raciColumns.map((col) => (
                    <th key={col.id} className="px-2 py-2 border-b border-l border-[var(--border-color)] min-w-[120px]">
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          className="w-full bg-transparent border-none text-xs font-medium text-[var(--text-primary)] focus:outline-none text-center"
                          value={col.name}
                          onChange={(e) => updateRaciColumnName(col.id, e.target.value)}
                          disabled={disabled}
                        />
                        {!disabled && (
                          <button type="button" className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => removeRaciColumn(col.id)}>
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  {!disabled && <th className="w-10 border-b border-l border-[var(--border-color)]" />}
                </tr>
              </thead>
              <tbody>
                {sowConfig.raciEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-[var(--bg-secondary)]/30">
                    <td className="px-3 py-1.5 border-b border-[var(--border-color)]">
                      <input
                        type="text"
                        className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1"
                        placeholder="Task name"
                        value={entry.task}
                        onChange={(e) => {
                          const store = useScopeStore.getState()
                          store.updateRaciEntry(entry.id, { task: e.target.value })
                        }}
                        disabled={disabled}
                      />
                    </td>
                    {sowConfig.raciColumns.map((col) => (
                      <td key={col.id} className={`px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center ${RACI_COLORS[entry.assignments[col.id] || '']}`}>
                        <select
                          className="bg-transparent text-xs font-bold text-center w-full focus:outline-none cursor-pointer disabled:cursor-default"
                          value={entry.assignments[col.id] || ''}
                          onChange={(e) => updateRaciAssignment(entry.id, col.id, e.target.value as RaciValue)}
                          disabled={disabled}
                        >
                          {RACI_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                    {!disabled && (
                      <td className="px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center">
                        <button type="button" className="p-1 text-red-400 hover:bg-red-400/10 rounded" onClick={() => removeRaciEntry(entry.id)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SowSection>

      {/* 5. Gantt Chart */}
      <SowSection id="gantt" expanded={expandedSections.gantt} onToggle={handleToggleSection} icon={BarChart3} iconColor="text-indigo-500" title={ganttSection?.formTitle || 'Gantt Chart'}>
        <SowSectionHeader sectionKey="gantt" title="Gantt Chart" />
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
            onClick={() => {
              if (sowConfig.ganttTasks.length > 0) {
                if (!confirm('This will replace the current Gantt tasks with the default template. Continue?')) return
              }
              loadDefaultGantt()
            }}
            disabled={disabled}
          >
            <Download size={14} />
            Load Default Template
          </button>
          {!disabled && (
            <button
              type="button"
              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
              onClick={() => addGanttTask(0)}
            >
              <Plus size={14} />
              Add Task
            </button>
          )}
        </div>

        {sowConfig.ganttTasks.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] text-center py-8 border border-dashed border-[var(--border-color)] rounded-lg">
            No Gantt tasks yet. Click "Load Default Template" to get started.
          </div>
        ) : (
          <>
            {/* Gantt table */}
            <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--bg-secondary)]">
                    {!disabled && <th className="w-16 border-b border-[var(--border-color)]" />}
                    <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[180px]">Task</th>
                    <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">Responsible</th>
                    <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[120px]">Party</th>
                    <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">Start Date</th>
                    <th className="text-left px-3 py-2 border-b border-l border-[var(--border-color)] font-medium text-[var(--text-primary)] min-w-[140px]">End Date</th>
                    {!disabled && <th className="w-10 border-b border-l border-[var(--border-color)]" />}
                  </tr>
                </thead>
                <tbody>
                  {sowConfig.ganttTasks.map((task, taskIdx) => (
                    <tr key={task.id} className="hover:bg-[var(--bg-secondary)]/30">
                      {!disabled && (
                        <td className="px-1 py-1.5 border-b border-[var(--border-color)] text-center">
                          <div className="flex flex-col items-center">
                            <button type="button" className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded disabled:opacity-30 disabled:cursor-default" disabled={taskIdx === 0} onClick={() => reorderGanttTask(task.id, 'up')} title="Move up">
                              <ChevronUp size={14} />
                            </button>
                            <button type="button" className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded disabled:opacity-30 disabled:cursor-default" disabled={taskIdx === sowConfig.ganttTasks.length - 1} onClick={() => reorderGanttTask(task.id, 'down')} title="Move down">
                              <ChevronDown size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                      <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                        <input type="text" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" placeholder="Task name" value={task.taskName} onChange={(e) => updateGanttTask(task.id, { taskName: e.target.value })} disabled={disabled} />
                      </td>
                      <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                        <input type="text" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" placeholder="Person name" value={task.responsiblePerson || ''} onChange={(e) => updateGanttTask(task.id, { responsiblePerson: e.target.value })} disabled={disabled} />
                      </td>
                      <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                        <select className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none cursor-pointer disabled:cursor-default" value={task.party || ''} onChange={(e) => updateGanttTask(task.id, { party: e.target.value as GanttParty | '' })} disabled={disabled}>
                          <option value="">-</option>
                          <option value="frankieone">FrankieOne</option>
                          <option value="client">Client</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                        <input type="date" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" value={task.startDate} onChange={(e) => updateGanttTask(task.id, { startDate: e.target.value })} disabled={disabled} />
                      </td>
                      <td className="px-2 py-1.5 border-b border-l border-[var(--border-color)]">
                        <input type="date" className="w-full bg-transparent text-sm text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-tertiary)] rounded px-1" value={task.endDate} onChange={(e) => updateGanttTask(task.id, { endDate: e.target.value })} disabled={disabled} />
                      </td>
                      {!disabled && (
                        <td className="px-1 py-1.5 border-b border-l border-[var(--border-color)] text-center">
                          <button type="button" className="p-1 text-red-400 hover:bg-red-400/10 rounded" onClick={() => removeGanttTask(task.id)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Gantt timeline visualization */}
            {(() => {
              const tasks = sowConfig.ganttTasks.filter((t) => t.startDate && t.endDate)
              if (tasks.length === 0) return null

              const dates = tasks.flatMap((t) => [new Date(t.startDate).getTime(), new Date(t.endDate).getTime()])
              const minDate = Math.min(...dates)
              const maxDate = Math.max(...dates)
              const range = maxDate - minDate || 1

              // Generate month labels
              const startMonth = new Date(minDate)
              startMonth.setDate(1)
              const monthLabels: { label: string; pct: number }[] = []
              const cursor = new Date(startMonth)
              while (cursor.getTime() <= maxDate) {
                const pct = ((cursor.getTime() - minDate) / range) * 100
                if (pct >= 0 && pct <= 100) {
                  monthLabels.push({ label: cursor.toLocaleString('default', { month: 'short', year: '2-digit' }), pct })
                }
                cursor.setMonth(cursor.getMonth() + 1)
              }

              return (
                <div className="mt-4">
                  <h5 className="text-sm font-medium text-[var(--text-primary)] mb-3">Timeline</h5>
                  {/* Month labels */}
                  <div className="relative h-6 mb-1">
                    {monthLabels.map((ml, i) => (
                      <span key={i} className="absolute text-[10px] text-[var(--text-muted)]" style={{ left: `calc(160px + ${ml.pct}% * (100% - 160px) / 100)` }}>
                        {ml.label}
                      </span>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {tasks.map((task) => {
                      const start = new Date(task.startDate).getTime()
                      const end = new Date(task.endDate).getTime()
                      const leftPct = ((start - minDate) / range) * 100
                      const widthPct = Math.max(((end - start) / range) * 100, 2)
                      const barColor = task.party === 'client' ? 'bg-amber-400' : 'bg-blue-500'

                      return (
                        <div key={task.id} className="flex items-center gap-2 text-xs">
                          <span className="w-40 flex-shrink-0 truncate text-[var(--text-muted)]">{task.taskName}</span>
                          <div className="flex-1 h-6 bg-[var(--bg-tertiary)] rounded relative">
                            <div
                              className={`absolute h-full rounded ${barColor} opacity-80`}
                              style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-500 opacity-80" /> FrankieOne</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-400 opacity-80" /> Client</span>
                  </div>
                </div>
              )
            })()}
          </>
        )}
      </SowSection>

      {/* 6. Key Assumptions */}
      <SowSection id="keyAssumptions" expanded={expandedSections.keyAssumptions} onToggle={handleToggleSection} icon={ClipboardList} iconColor="text-amber-500" title={keyAssumptionsSection?.formTitle || 'Key Assumptions'}>
        <SowSectionHeader sectionKey="keyAssumptions" title="Key Assumptions" />
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Key Assumptions</label>
          <p className="text-xs text-[var(--text-muted)] mb-2">
            List any key assumptions specific to this SOW (e.g., access to test environments, stable integration endpoints, timely approvals).
          </p>
          <textarea
            className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
            placeholder="e.g., Client will provide timely access to test environments; integration endpoints are stable..."
            value={sowConfig.keyAssumptions || ''}
            onChange={(e) => updateSowConfig('keyAssumptions', e.target.value)}
            disabled={disabled}
          />
          {(() => {
            const V2_CUTOFF = '2026-08-31'
            const v2Feats: string[] = []
            if (kybConfig?.internationalEnabled === 'yes') v2Feats.push('International KYB')
            if (kybConfig?.trustAnalyserEnabled === 'yes') v2Feats.push('Trust Analyser')
            if (kybConfig?.onboardingFormEnabled === 'yes') v2Feats.push('Business Onboarding Form')
            if (kybConfig?.amlEntityScreening === 'yes') v2Feats.push('Business AML')
            if (v2Feats.length > 0 && generalInfo.goLiveDate && generalInfo.goLiveDate < V2_CUTOFF) {
              return (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-amber-400">Auto-included in SOW: V2 Feature Exclusion</p>
                      <p className="text-xs text-amber-300/80 mt-1">
                        The following V2 features are scoped but will not be available before the go-live date ({generalInfo.goLiveDate}). This note will be automatically included in the exported SOW:
                      </p>
                      <div className="mt-2 p-2 rounded bg-amber-500/10 text-xs text-[var(--text-secondary)] italic">
                        The following features are currently in development and will not be available for implementation as part of the initial go-live: {v2Feats.join(', ')}. These features will be delivered in a subsequent phase once generally available.
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
            return null
          })()}
        </div>
      </SowSection>

      {/* 7. Additional Notes */}
      <div className="bg-[var(--bg-tertiary)]/40 border border-[var(--border-color)] rounded-lg p-4">
        <SowSectionHeader sectionKey="notes" title="Additional Notes" />
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Additional Notes</label>
        <textarea
          className="w-full min-h-32 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          placeholder="e.g., Special pricing arrangements, dependencies on client actions, assumptions..."
          value={sowConfig.notes}
          onChange={(e) => updateSowConfig('notes', e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  )
}
